// Node.js transport adapter for webserial-flasher.
// Works with the `serialport` npm package (https://serialport.io).
//
// Usage:
//   import { SerialPort } from 'serialport';
//   const port = new SerialPort({ path: '/dev/ttyACM0', baudRate: 115200, autoOpen: false });
//   await port.open();  // or pass openImmediately:true
//   const transport = new NodeSerialTransport(port);
//   const stk = new STK500(transport, board);
//   await stk.bootload(hex);
//   await transport.close();
export class NodeSerialTransport {
    constructor(port) {
        this.port = port;
        // Map user handlers → wrapped handlers so removeListener works correctly
        this.handlerMap = new Map();
    }
    async write(data) {
        return new Promise((resolve, reject) => {
            this.port.write(data, (err) => {
                if (err)
                    reject(err);
                else
                    resolve();
            });
        });
    }
    on(_event, handler) {
        const wrapped = (buf) => handler(new Uint8Array(buf));
        this.handlerMap.set(handler, wrapped);
        this.port.on('data', wrapped);
    }
    off(_event, handler) {
        const wrapped = this.handlerMap.get(handler);
        if (wrapped) {
            this.port.removeListener('data', wrapped);
            this.handlerMap.delete(handler);
        }
    }
    async setSignals(opts) {
        if (!this.port.set)
            return; // Port implementation doesn't support signal control
        return new Promise((resolve, reject) => {
            this.port.set(opts, (err) => {
                if (err)
                    reject(err);
                else
                    resolve();
            });
        });
    }
    /**
     * Send a BREAK condition for UPDI.
     *
     * Uses hardware SET{brk} if the port supports it (serialport >= 10).
     * Holds TX low for ~12 ms (≥ 12 bit-times at any supported baud rate).
     */
    async sendBreak() {
        if (!this.port.set) {
            throw new Error('NodeSerialTransport: sendBreak() requires port.set() ' +
                '(not available on this port implementation)');
        }
        await new Promise((resolve, reject) => {
            this.port.set({ brk: true }, (err) => {
                if (err)
                    reject(err);
                else
                    resolve();
            });
        });
        await new Promise((r) => setTimeout(r, 12)); // ≥12 ms = ≥12 bit-times @ 1 Mbaud
        await new Promise((resolve, reject) => {
            this.port.set({ brk: false }, (err) => {
                if (err)
                    reject(err);
                else
                    resolve();
            });
        });
        await new Promise((r) => setTimeout(r, 2)); // settling
    }
    async close() {
        this.handlerMap.clear();
        if (this.port.drain) {
            await new Promise((resolve) => {
                this.port.drain((err) => {
                    if (err) { /* ignore drain errors on close */ }
                    resolve();
                });
            });
        }
        if (this.port.close) {
            await new Promise((resolve) => {
                this.port.close((err) => {
                    if (err) { /* ignore */ }
                    resolve();
                });
            });
        }
    }
}
//# sourceMappingURL=NodeSerialTransport.js.map